import { Stack } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { BoxForm, CodeField, NumberField, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { SingleAdViewTimeSoftValidator } from 'features/globalSetup/pages/Defaults/RichPlacements/Sections/MultiAnchorSection/SingleAdViewTimeSoftValidator'
import { useAbility } from 'providers/casl'
import { AdUnitConnectionSection } from './AdUnitConnectionSection'
import { KNOWLEDGE_BASE_URL } from './constants'
import { Schema } from './schema'
import { TriggersSection } from './TriggersSection'
import { useGetWorkspaceDefaults, WorkspaceDefaults } from './useGetWorkspaceDefaults'
import { AppearanceWorkspaceDefaults } from './WorkspaceDefaults/AppearanceWorkspaceDefaults'
import { BasicSettingsWorkspaceDefaults } from './WorkspaceDefaults/BasicSettingsWorkspaceDefaults'

export const MultiAnchorSection = (): JSX.Element => {
  const ability = useAbility()
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.richPlacements.multiAnchor',
  })

  const { control } = useFormContext<Schema>()
  const { adUnitIds, customizeAppearance, customizeBasicSettings } = useWatch<Schema>()

  const cannotUpdate: boolean = ability.cannot('update', 'Domain')

  const workspaceDefaults: undefined | WorkspaceDefaults = useGetWorkspaceDefaults()

  const tooFewAdUnits: boolean = adUnitIds?.right !== undefined && adUnitIds.right.length < 2

  return (
    <>
      <Paper
        data-cy='section-multi-anchor'
        sx={{ mt: 0 }}
      >
        <SectionHeader
          subtitle={t('enabled.description')}
          title={t('enabled.title')}
          titleEndAdornment={
            <Switch
              control={control}
              disabled={cannotUpdate || tooFewAdUnits}
              name='enabled'
              {...(tooFewAdUnits && {
                tooltip: {
                  content: t('enabled.tooltip'),
                },
                tooltipMode: 'wrap',
                uiValueOverride: false,
              })}
            />
          }
        />
      </Paper>

      <Paper data-cy='section-basic-settings'>
        <SectionHeader
          subtitle={t('basicSettings.subtitle')}
          title={t('basicSettings.title')}
        />

        <GapBox />

        <Stack
          alignItems='flex-start'
          direction='row'
          spacing={2}
        >
          <BoxForm>
            <Switch
              control={control}
              disabled={cannotUpdate}
              label={t('basicSettings.customizeBasicSettings.label')}
              labelPlacement='start'
              name='customizeBasicSettings'
              tooltip={{ content: t('basicSettings.customizeBasicSettings.tooltip') }}
            />

            {customizeBasicSettings && (
              <>
                <NumberField
                  control={control}
                  disabled={cannotUpdate}
                  helperText={<SingleAdViewTimeSoftValidator />}
                  label={t('basicSettings.singleAdViewTime.label')}
                  name='singleAdViewTime'
                  tooltip={{ content: t('basicSettings.singleAdViewTime.tooltip') }}
                  width='md'
                />
                <Switch
                  control={control}
                  disabled={cannotUpdate}
                  label={t('basicSettings.displayAfterUserInteraction.label')}
                  labelPlacement='start'
                  name='displayAfterUserInteraction'
                  tooltip={{ content: t('basicSettings.displayAfterUserInteraction.tooltip') }}
                />
              </>
            )}
          </BoxForm>

          {customizeBasicSettings && workspaceDefaults && (
            <BasicSettingsWorkspaceDefaults workspaceDefaults={workspaceDefaults} />
          )}
        </Stack>
      </Paper>

      <Paper data-cy='section-appearance'>
        <SectionHeader
          subtitle={t('appearance.subtitle')}
          title={t('appearance.title')}
        />

        <GapBox />

        <BoxForm>
          <Stack
            alignItems='flex-start'
            direction='row'
            spacing={2}
          >
            <BoxForm>
              <Switch
                control={control}
                disabled={cannotUpdate}
                label={t('appearance.customizeAppearance.label')}
                labelPlacement='start'
                name='customizeAppearance'
                tooltip={{ content: t('appearance.customizeAppearance.tooltip') }}
              />

              {customizeAppearance && (
                <>
                  <Switch
                    control={control}
                    disabled={cannotUpdate}
                    label={t('appearance.showCloseButton.label')}
                    labelPlacement='start'
                    name='showCloseButton'
                    tooltip={{ content: t('appearance.showCloseButton.tooltip') }}
                  />
                  <Switch
                    control={control}
                    disabled={cannotUpdate}
                    label={t('appearance.fullWidthMode.label')}
                    labelPlacement='start'
                    name='fullWidthMode'
                    tooltip={{ content: t('appearance.fullWidthMode.tooltip') }}
                  />
                </>
              )}
            </BoxForm>

            {customizeAppearance && workspaceDefaults && (
              <AppearanceWorkspaceDefaults workspaceDefaults={workspaceDefaults} />
            )}
          </Stack>

          {customizeAppearance && (
            <BoxForm>
              <CodeField
                control={control}
                disabled={cannotUpdate}
                language='json'
                name='customStyling'
                title={t('appearance.customStyling.label')}
                tooltip={{
                  content: t('appearance.customStyling.tooltip'),
                  link: {
                    title: tc('learnMore'),
                    url: KNOWLEDGE_BASE_URL.CUSTOM_STYLING,
                  },
                }}
              />
            </BoxForm>
          )}
        </BoxForm>
      </Paper>

      <TriggersSection />

      {ability.can('read', 'DomainRichPlacementsFeature-multiAnchorAdUnitConnection') && (
        <AdUnitConnectionSection />
      )}
    </>
  )
}
